<template>
    <v-dialog :model-value="modelValue" max-width="460" @update:model-value="(v) => !saving && emit('update:modelValue', v)">
        <v-card>
            <v-card-title class="text-h6">{{ title }}</v-card-title>
            <v-card-text>
                <div v-if="pending.length === 1" class="text-body-2 text-medium-emphasis mb-3">
                    {{ pending[0].label }}<span v-if="pending[0].asset?.label"> on {{ pending[0].asset.label }}</span>
                </div>
                <v-radio-group v-model="resolution" hide-details class="mb-2">
                    <v-radio v-for="r in RESOLUTIONS" :key="r.value" :value="r.value" :label="r.title"/>
                </v-radio-group>
                <v-textarea v-model="note" label="Note (optional)" rows="2" auto-grow variant="outlined"
                            density="compact" counter="1000" maxlength="1000" hide-details="auto"/>
                <div v-if="pending.some((d) => d.service_task_id)" class="text-caption text-medium-emphasis mt-2">
                    A linked pending work order is marked completed too.
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer/>
                <v-btn variant="text" :disabled="saving" @click="emit('update:modelValue', false)">Cancel</v-btn>
                <v-btn color="success" variant="flat" :loading="saving" :disabled="saving" @click="save">Resolve</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import {refreshMenuCounts} from "@/composables/useMenuCounts";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {apiError} from "@/components/drivers/shifts/shiftFormat";
import {RESOLUTIONS} from "./inspectionFormat";

// Resolves one defect, or several with the same reason and note.
const props = defineProps({
    modelValue: {type: Boolean, default: false},
    defects: {type: Array, default: () => []},
});
const emit = defineEmits(["update:modelValue", "resolved"]);
const {showSnackbar} = useSnackbarStore();

const resolution = ref("repaired");
const note = ref("");
const saving = ref(false);

// The defects still to resolve: after a partial failure only the failed ones stay.
const pending = ref([]);

const title = computed(() => (pending.value.length > 1 ? `Resolve ${pending.value.length} defects` : "Resolve defect"));

watch(() => props.modelValue, (open) => {
    if (!open) return;
    pending.value = props.defects.slice();
    resolution.value = "repaired";
    note.value = "";
});

const save = async () => {
    saving.value = true;
    const body = {resolution: resolution.value, resolution_note: (note.value || "").trim() || null};
    const done = [];
    const failed = [];
    let error = null;
    for (const d of pending.value) {
        try {
            await send("post", `fleet/inspection-defects/${d.id}/resolve`, body);
            done.push(d.id);
        } catch (e) {
            failed.push(d);
            error = e;
        }
    }
    saving.value = false;
    if (done.length) {
        refreshMenuCounts();
        emit("resolved", done);
    }
    if (error) {
        pending.value = failed;
        const fallback = failed.length === 1 ? "Could not resolve the defect." : `Could not resolve ${failed.length} defects.`;
        const prefix = done.length ? `${done.length} resolved. ` : "";
        showSnackbar(prefix + apiError(error, fallback), "error");
        return;
    }
    showSnackbar(done.length === 1 ? "Defect resolved." : `${done.length} defects resolved.`, "success");
    emit("update:modelValue", false);
};
</script>
